/* eslint-disable no-unused-vars */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-useless-return */
/* eslint-disable no-console */
import { reminderFields, laneReminderFields } from 'src/constants/fields';
import { practiceInSuffix } from 'src/utils/practiceInSuffix';
import { dispatch, store } from '../../store';
import { updateError } from '../user';
import { callApi } from '../../../utils/apiCall';
import { errorHandler } from '../../../utils/errorHandler';
import { isOldRemindersDisabled, EMPTY_REMINDERS } from '../../../utils/oldRemindersDisabled';
import { leadActions, startLoadingData, updateCompleteStatus, setLoadingApis } from './slice';
import { setLoader } from '../loader';

// Helper: pull the current All-Practices selection off the store and
// translate to `&practice__in=...` (empty string for single-practice).
// Shared by every reminders GET in this file so the tasks sidebar and
// lane counts match the multi-practice lead tables.
const _activePracticesSuffix = () =>
    practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds);

// Lane boundary DATES must be computed in Europe/London — the BE's
// `datetime__date` filter evaluates in settings.TIME_ZONE (Europe/London),
// so using the browser's local date would mis-lane late-evening reminders for
// any user whose local date differs from London's (different TZ, or the
// cross-midnight window). moment-timezone is not a dependency, so we derive
// London's calendar date via Intl and do the ±N-day arithmetic on a UTC-noon
// anchor (DST-safe — adding days at noon never crosses into an adjacent day).
// Returns "YYYY-MM-DD".
const londonDateShifted = (deltaDays = 0) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
    const [y, m, d] = parts.split('-').map(Number);
    const anchor = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    anchor.setUTCDate(anchor.getUTCDate() + deltaDays);
    const pad = (n) => String(n).padStart(2, '0');
    return `${anchor.getUTCFullYear()}-${pad(anchor.getUTCMonth() + 1)}-${pad(anchor.getUTCDate())}`;
};

// Shared group-code helpers used by all reminder URL builders in this file.
const REMINDER_GROUP_CODES = ['TODO', 'FOLLOW_UP', 'NEW_LEAD', 'CALLBACK', 'CALLBACK_REQUEST', 'UNANSWERED', 'OTHER'];

const isGroupCode = (v) => REMINDER_GROUP_CODES.includes(v);

const resolveGroupIn = (filter, dataContext, fallback) => {
    if (filter === 'CALLBACK' || filter === 'CALLBACK_REQUEST') return 'CALLBACK_REQUEST';
    if (isGroupCode(filter)) return filter;
    if (dataContext === 'dashboard') return 'FOLLOW_UP,TODO,NEW_LEAD';
    return fallback || 'TODO,FOLLOW_UP,CALLBACK_REQUEST,NEW_LEAD,UNANSWERED,OTHER';
};

const resolveStatusParam = (filter, leadStatus) => {
    if (leadStatus) return `&lead_treatment__status=${leadStatus}`;
    if (filter == null || filter === '' || filter === 'All' || filter === 'leadNotes' || filter === 'undefined' || String(filter) === 'undefined') return '';
    if (isGroupCode(filter)) return '';
    return `&lead_treatment__status=${filter}`;
};

// Non-blocking helper: completes prior FOLLOW_UP/NEW_LEAD reminders before a new FOLLOW_UP is created.
// Failure here does not block new reminder creation — better to have a stale duplicate than to fail the whole flow.
async function completeActiveReminders(practiceId, leadTreatmentId) {
    if (!practiceId || !leadTreatmentId || Number.isNaN(Number(leadTreatmentId))) return;
    try {
        const response = await callApi(
            `/api/v1/practices/${practiceId}/reminders/?lead_treatment=${leadTreatmentId}&is_completed=false&group__in=FOLLOW_UP,NEW_LEAD&limit=50`
        );
        if (response?.status === 200 && response.data?.results?.length > 0) {
            const results = await Promise.allSettled(
                response.data.results
                    .filter(reminder => reminder.id)
                    .map(reminder =>
                        callApi(`/api/v1/practices/${practiceId}/reminders/${reminder.id}/`, 'patch', { is_completed: true })
                    )
            );
            const failed = results.filter(r => r.status === 'rejected' || (r.value && r.value.status >= 400));
            if (failed.length > 0) {
                console.warn(`completeActiveReminders: ${failed.length}/${results.length} PATCHes failed`);
            }
        }
    } catch (error) {
        console.warn('Failed to auto-complete previous reminders:', error?.message);
    }
}

export function addReminder(data, practiceId, handleClose, value) {
    return async () => {
        try {
            if (data?.method?.toLowerCase() === "no-reminder") {
                if (value === "createLead") return;
                dispatch(updateCompleteStatus(false));
                if (value === 'unable') {
                    dispatch(updateError({ success: true, message: 'Notes added successfully', reminder: false }));
                } else {
                    dispatch(updateError({ success: true, message: 'No reminder is set', reminder: false }));
                }
                if (value === 'LeadDetail') {
                    handleClose();
                    return;
                }
                handleClose('finish', data?.lead);
                dispatch(startLoadingData(false));
                return;
            }
            // Creating a lead can already create checklist tasks. Do not complete
            // those tasks while attaching the optional initial reminder.
            if (data?.group === 'FOLLOW_UP' && value !== 'createLead') {
                await completeActiveReminders(practiceId, data?.lead_treatment);
            }
            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/`, 'post', data);
            if (response?.status === 201) {

                if (value === "createLead") return;

                dispatch(updateCompleteStatus(false));
                if (value === 'unable') {
                    dispatch(updateError({ success: true, message: 'Note and Reminder added successfully', reminder: false }));
                } else {
                    dispatch(updateError({ success: true, message: 'Reminder added successfully', reminder: false }));
                }
                if (value === 'LeadDetail') {
                    handleClose();
                    return;
                }
                handleClose('finish', data?.lead);
                dispatch(startLoadingData(false));
            } else {
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ message: JSON.stringify(response.data), success: false, status: response?.status }));
                dispatch(startLoadingData(false));
                // Clear the global Finish spinner — the outer flow
                // (UnableToContact.confirmHandleSubmit) sets isLoader=true
                // before submit and relies on the success callback to
                // clear it. Non-2xx responses skipped that path and left
                // the button stuck.
                dispatch(setLoader(false));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error instanceof Error ? new Error(error.message) : new Error(String(error?.message ?? 'error')) }));
            dispatch(updateCompleteStatus(false));
            dispatch(startLoadingData(false));
            // Same rationale as the non-2xx branch above.
            dispatch(setLoader(false));
        }
    };
}

function settleLeadDetailReminders(filter, grater) {
    if (filter === 'leadNotes' || grater === 'leadNotes') {
        dispatch(leadActions.AddLeadNotes(EMPTY_REMINDERS));
    }
    if (grater !== 'firstTime' && grater !== 'Graterthan') {
        dispatch(leadActions.leadReminderData(EMPTY_REMINDERS));
    }
}

export function getLeadReminderData(practiceId, data, limit, offset, filter, id, assigned, handleNext, grater, counter, additionalfilters, tasksCompletedUser, isLeadReminderFlow, taskDateRange, taskUnassignUser, treatment, leadStatus, searchQuery, dueNowWholePractice = false) {
    return async () => {
        try {
            dispatch(startLoadingData(true));

            // ── Per-lane fetch for Tasks page and Dashboard (fresh load) ──
            // Skip per-lane only for single-lane filters (Overdue/Upcoming) or custom date ranges
            const isSingleLaneFilter = additionalfilters && ['Overdue', 'Upcoming'].includes(additionalfilters);
            const isDashboardOrTasks = data === 'Tasks' || data === 'dashboard';
            if (isDashboardOrTasks && offset === 0 && !isSingleLaneFilter && !taskDateRange) {
                const laneLimit = data === 'dashboard' ? 50 : 100;
                const fields = `&fields=${laneReminderFields}`;
                // ── "Due now — whole practice" mode (dashboard 'New patient follow ups'
                // widget, DASHBOARD_WIDGET_COUNTER key '2') ──
                // The widget badge means today+overdue across ALL staff. To make the
                // click-through list match that badge exactly, this entry point forces:
                //   • no assignee filter (whole practice, all staff), and
                //   • drop the future/scheduled lane (today+overdue only).
                // Scoped to this flag so the normal To-do tab (direct tab-strip click,
                // per-user) is untouched.
                const assignedParam = (assigned && !dueNowWholePractice) ? `&assignees__user__id__in=${assigned}` : '';
                const statusParam = resolveStatusParam(filter, leadStatus);
                const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
                const unassignedParam = taskUnassignUser ? '&assignees__isnull=true' : '';
                const treatmentParam = treatment ? `&lead_treatment__treatment__in=${treatment}` : '';
                const isCompleted = tasksCompletedUser === undefined ? false : tasksCompletedUser;
                const baseUrl = `/api/v1/practices/${practiceId}/reminders/?group__in=${resolveGroupIn(filter, data)}&is_completed=${isCompleted}${assignedParam}&ordering=datetime${statusParam}${fields}&lead__is_archived=false${unassignedParam}${treatmentParam}${searchParam}${_activePracticesSuffix()}`;
                // Lane boundaries in Europe/London (see londonDateShifted above) so
                // they match the BE's datetime__date filter timezone.
                const today = londonDateShifted(0);
                const yesterday = londonDateShifted(-1);
                const tomorrow = londonDateShifted(1);

                // When 'Today' filter is active, skip future/scheduled lane.
                // "Due now — whole practice" also drops the scheduled lane so the
                // list (overdue + today) matches the widget badge.
                const includeScheduled = !dueNowWholePractice && (!additionalfilters || additionalfilters === '' || additionalfilters === 'All');
                // Today lane capped at laneLimit — same as other lanes for dashboard.
                // Tasks page keeps its own higher cap via laneLimit=100.
                const todayLaneLimit = data === 'dashboard' ? 50 : 1000;
                // "Due now — whole practice" renders the full today+overdue set so the
                // visible list count equals the widget badge (no 50-row dashboard cap).
                // Only this flagged entry point uses the higher cap; every other
                // dashboard/Tasks lane fetch keeps its existing limit.
                const overdueLimit = dueNowWholePractice ? 1000 : laneLimit;
                const todayLimit = dueNowWholePractice ? 1000 : todayLaneLimit;
                const laneCalls = [
                    callApi(`${baseUrl}&datetime__date__lte=${yesterday}&limit=${overdueLimit}&offset=0`),
                    callApi(`${baseUrl}&datetime__date__gte=${today}&datetime__date__lte=${today}&limit=${todayLimit}&offset=0`),
                ];
                if (includeScheduled) {
                    laneCalls.push(callApi(`${baseUrl}&datetime__date__gte=${tomorrow}&limit=${laneLimit}&offset=0`));
                }
                const [overdueRes, todayRes, scheduledRes] = await Promise.all(laneCalls);

                const overdueResults = overdueRes?.status === 200 ? overdueRes.data?.results || [] : [];
                const todayResults = todayRes?.status === 200 ? todayRes.data?.results || [] : [];
                const scheduledResults = includeScheduled && scheduledRes?.status === 200 ? scheduledRes.data?.results || [] : [];

                const merged = {
                    count: (overdueRes?.data?.count || 0) + (todayRes?.data?.count || 0) + (includeScheduled ? scheduledRes?.data?.count || 0 : 0),
                    results: [...overdueResults, ...todayResults, ...scheduledResults],
                };
                const laneCounts = {
                    doNow: overdueRes?.data?.count || 0,
                    today: todayRes?.data?.count || 0,
                    scheduled: includeScheduled ? scheduledRes?.data?.count || 0 : 0,
                };

                if (data === 'dashboard') {
                    // Dashboard uses allReminders state for the merged list, but we also
                    // dispatch laneCounts so the lane badges can show true API counts
                    // instead of `lane.tasks.length` (which is capped at the per-lane fetch).
                    dispatch(leadActions.getAllReminderData(merged));
                    dispatch(leadActions.setTaskLaneCounts(laneCounts));
                } else {
                    dispatch(leadActions.setTaskPageLaneData({ merged, laneCounts }));
                }
                dispatch(startLoadingData(false));
                return;
            }

            const dateFilter = taskDateRange ? `&datetime__date__range=${taskDateRange}` : '';
            const unassignedUserFilter = taskUnassignUser ? '&assignees__isnull=true' : '';
            const nonLaneStatusParam = resolveStatusParam(filter, leadStatus);
            const nonLaneSearchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
            const param = `&limit=${limit}&offset=${offset}${id && !Number.isNaN(Number(id)) ? `&lead=${id}` : ''}${assigned && `&assignees__user__id__in=${assigned}`}&ordering=datetime${nonLaneStatusParam}${additionalfilters === 'Overdue' ? `&datetime__date__lte=${londonDateShifted(-1)}` : (grater === 'Graterthan' || grater === 'firstTime') && !additionalfilters ? `&datetime__date__gte=${londonDateShifted(0)}` : additionalfilters === 'Upcoming' ? `&datetime__date__gte=${londonDateShifted(1)}` : additionalfilters === 'Today' ? `&datetime__date__lte=${londonDateShifted(0)}` : ''}`;
            const fields = `&fields=${reminderFields}`;
            // LeadDetail uses a narrower default group set; all other contexts use resolveGroupIn's full fallback.
            const nonLaneGroupIn = data === 'LeadDetail' ? resolveGroupIn(filter, null, 'TODO,FOLLOW_UP,NEW_LEAD,UNANSWERED,CALLBACK_REQUEST,OTHER') : resolveGroupIn(filter, data);
            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/?group__in=${nonLaneGroupIn}&is_completed=${tasksCompletedUser === undefined ? false : tasksCompletedUser}${param}${fields}&lead__is_archived=false${dateFilter}${unassignedUserFilter}${treatment ? `&lead_treatment__treatment__in=${treatment}` : ''}${nonLaneSearchParam}${_activePracticesSuffix()}`);

            if (response?.status === 200) {
                const result = response.data;

                if (data === 'LeadDetail') {
                    if (counter === 'counter') {
                        dispatch(leadActions.addTaskCounter(result?.count));
                    }
                    if (grater === 'firstTime') {
                        dispatch(leadActions.upComingTask(result?.results.length > 0 && result?.results[0]));
                        return;
                    }
                    if (grater === 'Graterthan') {
                        dispatch(leadActions.AddLeadNotes(result));
                        dispatch(leadActions.upComingTask(result?.results.length > 0 && result?.results[0]));
                        return;
                    }
                    if (filter === 'leadNotes' || grater === 'leadNotes') {
                        dispatch(leadActions.AddLeadNotes(result));
                    }
                    dispatch(leadActions.leadReminderData(result));
                    if (isLeadReminderFlow) {
                        if (offset === 0) {
                            dispatch(leadActions.leadFlowReminderData(result));
                            return;
                        }
                        dispatch(leadActions.leadFlowReminderMoreData(result));
                    }
                }
                if (data === 'dashboard') {
                    dispatch(leadActions.getAllReminderData(result));
                }
                if (data === 'Tasks') {
                    dispatch(leadActions.getTasksPageData(result));
                }
            }
            else {
                // Lead detail gates its loader on these slices being non-null; a failed
                // background fetch must still settle them (empty) or the page stays blurred.
                if (data === 'LeadDetail') settleLeadDetailReminders(filter, grater);
                // Old reminder system disabled for the practice: treat as empty, no toast.
                if (!isOldRemindersDisabled(response)) {
                    dispatch(updateError({ message: JSON.stringify(response?.data || 'Something went wrong'), success: false, status: response?.status }));
                }
                return;
            }

        }

        catch (error) {
            if (data === 'LeadDetail') settleLeadDetailReminders(filter, grater);
            // Callers pass '' for handleNext; `?.` alone would throw inside this catch.
            if (typeof handleNext === 'function') handleNext({
                success: false,
                message: JSON.stringify(error?.message || error) || 'Something went wrong'
            });
            dispatch(leadActions.hasError(error?.message || error));

        }
        finally {
            dispatch(startLoadingData(false));
        }
    }
};


export function loadMoreTasksPageData(practiceId, limit, offset, filter, assigned, additionalfilters, tasksCompletedUser, taskDateRange, taskUnassignUser, treatment, leadStatus, searchQuery) {
    return async () => {
        try {
            const dateFilter = taskDateRange ? `&datetime__date__range=${taskDateRange}` : '';
            const unassignedUserFilter = taskUnassignUser ? '&assignees__isnull=true' : '';
            const statusParam = resolveStatusParam(filter, leadStatus);
            const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
            const param = `&limit=${limit}&offset=${offset}${assigned ? `&assignees__user__id__in=${assigned}` : ''}&ordering=datetime${statusParam}`;
            const fields = `&fields=${reminderFields}`;
            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/?group__in=${resolveGroupIn(filter, null)}&is_completed=${tasksCompletedUser === undefined ? false : tasksCompletedUser}${param}${fields}&lead__is_archived=false${dateFilter}${unassignedUserFilter}${treatment ? `&lead_treatment__treatment__in=${treatment}` : ''}${searchParam}${_activePracticesSuffix()}`);
            if (response?.status === 200) {
                dispatch(leadActions.appendTasksPageData(response.data));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error?.message || error));
        }
    };
}

export function loadMoreTasksLaneData(practiceId, lane, limit, offset, filter, assigned, tasksCompletedUser, taskUnassignUser, treatment, leadStatus, searchQuery) {
    return async () => {
        try {
            // Europe/London boundaries — MUST match getLeadReminderData's lanes so
            // "load more" paginates the same lane the initial fetch populated.
            const today = londonDateShifted(0);
            const yesterday = londonDateShifted(-1);
            const tomorrow = londonDateShifted(1);
            let laneDate = '';
            if (lane === 'doNow') laneDate = `&datetime__date__lte=${yesterday}`;
            else if (lane === 'today') laneDate = `&datetime__date__gte=${today}&datetime__date__lte=${today}`;
            else if (lane === 'scheduled') laneDate = `&datetime__date__gte=${tomorrow}`;
            const assignedParam = assigned ? `&assignees__user__id__in=${assigned}` : '';
            const statusParam = resolveStatusParam(filter, leadStatus);
            const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
            const unassignedParam = taskUnassignUser ? '&assignees__isnull=true' : '';
            const treatmentParam = treatment ? `&lead_treatment__treatment__in=${treatment}` : '';
            const isCompleted = tasksCompletedUser === undefined ? false : tasksCompletedUser;
            const fields = `&fields=${reminderFields}`;
            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/?group__in=${resolveGroupIn(filter, null)}&is_completed=${isCompleted}&limit=${limit}&offset=${offset}${assignedParam}&ordering=datetime${statusParam}${fields}&lead__is_archived=false${laneDate}${unassignedParam}${treatmentParam}${searchParam}${_activePracticesSuffix()}`);
            if (response?.status === 200) {
                dispatch(leadActions.appendTasksLaneData({ results: response.data?.results || [], lane, limit, offset }));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error?.message || error));
        }
    };
}

export function loadDashboardScheduledTasks(practiceId, filter, assigned, tasksCompletedUser, taskUnassignUser, treatment, appendToTasksPage = false, searchQuery = '') {
    return async () => {
        try {
            // Europe/London — matches the 'scheduled' lane boundary elsewhere.
            const tomorrow = londonDateShifted(1);
            const assignedParam = assigned ? `&assignees__user__id__in=${assigned}` : '';
            const statusParam = resolveStatusParam(filter, null);
            const unassignedParam = taskUnassignUser ? '&assignees__isnull=true' : '';
            const treatmentParam = treatment ? `&lead_treatment__treatment__in=${treatment}` : '';
            const isCompleted = tasksCompletedUser === undefined ? false : tasksCompletedUser;
            const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
            const fields = `&fields=${reminderFields}`;
            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/?group__in=${resolveGroupIn(filter, 'dashboard')}&is_completed=${isCompleted}${assignedParam}&ordering=datetime${statusParam}${fields}&lead__is_archived=false&datetime__date__gte=${tomorrow}&limit=20&offset=0${unassignedParam}${treatmentParam}${searchParam}${_activePracticesSuffix()}`);
            if (response?.status === 200) {
                dispatch(leadActions.appendDashboardScheduledData(response.data?.results || []));
                if (appendToTasksPage) {
                    dispatch(leadActions.appendTasksPageData(response.data));
                }
            }
        } catch (error) {
            dispatch(leadActions.hasError(error?.message || error));
        }
    };
}

export const handleTaskApiLoading = (filter) => {
    const { loadingApis } = store.getState().lead;
    if (filter === '') {
        dispatch(setLoadingApis(loadingApis.filter((ele) => ele !== 'All')));
    } else {
        dispatch(setLoadingApis(loadingApis.filter((ele) => ele !== filter)));
    }
};

export function leadTaskListCounter(practiceId, type, filter, id, assigned, additionalFilter, tasksCompletedUser, isFlowCounter, taskDateRange, taskUnassignUser, treatment) {
    return async () => {
        try {
            dispatch(startLoadingData(true));
            const todayDateLimit = type === 'dashboard' && !taskDateRange ? `&datetime__date__lte=${londonDateShifted(0)}` : '';
            const dateFilter = taskDateRange ? `&datetime__date__range=${taskDateRange}` : todayDateLimit;
            const counterStatusParam = resolveStatusParam(filter, null);
            const param = `&limit=${1}&offset=${0}${id && !Number.isNaN(Number(id)) ? `&lead=${id}` : ''}${assigned ? `&assignees__user__id__in=${assigned}` : ''}&ordering=datetime${counterStatusParam}`;
            const unassignedUserFilter = taskUnassignUser ? '&assignees__isnull=true' : '';
            const isCompletedParam = tasksCompletedUser === undefined ? false : tasksCompletedUser;
            // LeadDetail uses a narrower default group set; all other contexts use resolveGroupIn's full fallback.
            const counterGroupIn = type === 'LeadDetail' ? resolveGroupIn(filter, null, 'TODO,FOLLOW_UP,NEW_LEAD,UNANSWERED,CALLBACK_REQUEST,OTHER') : resolveGroupIn(filter, type === 'dashboard' ? 'dashboard' : null);
            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/?group__in=${counterGroupIn}&is_completed=${isCompletedParam}${param}&lead__is_archived=false${dateFilter}${unassignedUserFilter}${treatment ? `&lead_treatment__treatment__in=${treatment}` : ''}${_activePracticesSuffix()}`);
            if (response?.status === 200) {
                const result = response.data;
                const { taskCounter, taskLeadCounter, separateTaskCounter, taskFlowCounter } = store.getState().lead;
                const newEntry = { filter, count: result?.count };
                if (type === 'dashboard') {
                    dispatch(leadActions.updateTaskCounter([newEntry, ...taskCounter.filter(e => e.filter !== filter)]));
                }
                if (type === 'LeadDetail') {
                    dispatch(leadActions.updateLeadTaskCounter([newEntry, ...taskLeadCounter.filter(e => e.filter !== filter)]));
                }
                if (isFlowCounter) {
                    dispatch(leadActions.updateFlowTaskCounter([newEntry, ...taskFlowCounter.filter(e => e.filter !== filter)]));
                }
                if (type === 'Tasks') {
                    dispatch(leadActions.updateSeparateTaskCounter([newEntry, ...separateTaskCounter.filter(e => e.filter !== filter)]));
                }


            } else if (!isOldRemindersDisabled(response)) {
                dispatch(updateError({ message: response?.data || 'Something went wrong', success: false, status: response?.status }));
            }

        } catch (error) {
            dispatch(updateError({ message: error.message || 'Something went wrong', success: false, cause: error instanceof Error ? new Error(error.message) : new Error(String(error?.message ?? 'error')) }));
            dispatch(leadActions.hasError(error.message));
        }
        finally {
            handleTaskApiLoading(filter);
            dispatch(startLoadingData(false));
        }
    };
}

export function getReminderData(practiceId, id) {
    return async () => {
        try {

            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/${id}/?fields=${reminderFields}`);
            if (response?.status === 200) {
                dispatch(leadActions.getTaskDetails(response.data));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message }));
        }
    };
}

export function deleteReminderData(practiceId, id, handleSuccess, handleRefresh) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/${id}/`, 'delete');
            if (response?.status === 204) {
                handleSuccess({ success: true, message: 'Reminder deleted successfully' });
                handleRefresh();
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message }));
        }
    };
}

export function AddReminder(data, practiceId, handleNext) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/`, 'post', data);
            if (response?.status === 201) {
                handleNext?.({ success: true, message: 'Reminder scheduled' });
            } else {
                const message = response?.data?.detail || `Failed to create reminder (HTTP ${response?.status || 'unknown'})`;
                handleNext?.({ success: false, message });
                dispatch(leadActions.hasError(message));
            }
        } catch (error) {
            handleNext?.({ success: false, message: error.message || 'Failed to create reminder' });
            dispatch(leadActions.hasError(error.message));
        }
    };
}

export function editReminder(data, id, practiceId, handleNext) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/${id}/`, 'put', data);
            if (response?.status === 200) {
                handleNext?.({ success: true, message: 'Reminder updated successfully' });
            } else {
                const message = response?.data && typeof response.data === 'object'
                    ? Object.entries(response.data).map(([k, v]) => `${k}: ${v}`).join(', ')
                    : response?.data?.detail || `Failed to update reminder (HTTP ${response?.status || 'unknown'})`;
                handleNext?.({ message, success: false });
                dispatch(leadActions.hasError(message));
            }
        } catch (error) {
            const message = error?.message || 'An error occurred while updating the reminder';
            handleNext?.({ message, success: false });
            dispatch(leadActions.hasError(message));
        }
    };
}

export function updateReminder(data, id, practiceId, handleRefresh, value, handleSuccess, isCompletedValue) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/${id}/`, 'patch', data);
            if (response?.status === 200) {
                handleRefresh(value);
                if (value === 'markComplete' && handleSuccess && !isCompletedValue) {
                    handleSuccess({ success: true, message: 'Task updated successfully !', });
                }
                if (isCompletedValue === 'isCompleted' && handleSuccess) {
                    handleSuccess({ success: true, message: 'Task not completed !', });
                }
            } else if (response?.status === 404) {
                if (handleSuccess) handleSuccess({ success: false, message: 'This task has been deleted or no longer exists.' });
                handleRefresh(value);
            } else {
                errorHandler(response.data, handleSuccess);
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function updateAssignToUser(data, id, practiceId, handleSuccess, type, updateType) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${id}/`, 'patch', data);
            if (response?.status === 200) {
                handleSuccess({ success: true, message: 'Lead updated successfully', event: type, leadUpdate: updateType });
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
                dispatch(updateCompleteStatus(false));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function addScheduleReminder(data, practiceId, handleNext) {
    return async () => {
        try {
            if (data?.method?.toLowerCase() === "no-reminder") {
                handleNext?.({ success: true, message: 'No reminder is set', reminder: false });
                return;
            }
            if (data?.group === 'FOLLOW_UP') {
                await completeActiveReminders(practiceId, data?.lead_treatment);
            }
            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/`, 'post', data);
            if (response?.status === 201) {
                handleNext?.({ success: true, message: 'Reminder added successfully', reminder: false });
                // Refresh the popup sidebar's reminder counter so it shows the new reminder
                // immediately (without requiring the user to close and reopen the popup).
                if (data?.lead) {
                    dispatch(getLeadReminderData(practiceId, 'LeadDetail', 10, 0, 'leadNotes', data.lead, '', '', '', 'counter', '', false, true));
                }
            } else {
                const message = response?.data && typeof response.data === 'object'
                    ? Object.entries(response.data).map(([k, v]) => `${k}: ${v}`).join(', ')
                    : response?.data?.detail || `Failed to create reminder (HTTP ${response?.status || 'unknown'})`;
                handleNext?.({ message, success: false });
                dispatch(leadActions.hasError(message));
            }
        } catch (error) {
            const message = error?.message || 'An error occurred while creating the reminder';
            handleNext?.({ message, success: false });
            dispatch(leadActions.hasError(message));
        }
    };
}

export function deleteTaskReminder(practiceId, id) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/${id}/`, 'delete');
            if (response?.status === 204) {
                dispatch(updateError({ message: 'Reminder delete successfully', success: true }));
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data), success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error?.message) || 'Something went wrong', success: false }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function bulkCreateReminders(practiceId, data, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/bulk-create/`, 'post', data);
            if (response?.status === 201) {
                handleSuccess({ success: true, data: response?.data });
            } else {
                handleSuccess({ success: false, error: response?.data });
            }
        } catch (error) {
            handleSuccess({ success: false, error: error.message });
        }
    };
}

export function fetchBulkLeadContext(practiceId, leadIds) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/bulk-context/`,
                'post',
                { lead_ids: leadIds }
            );
            if (response?.status === 200) {
                return { success: true, data: response.data };
            }
            return { success: false, error: response?.data };
        } catch (error) {
            return { success: false, error: error.message };
        }
    };
}

export function smartBulkCreateReminders(practiceId, reminders, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/reminders/smart-bulk-create/`,
                'post',
                { reminders }
            );
            if (response?.status === 201) {
                handleSuccess({ success: true, data: response?.data });
            } else {
                handleSuccess({ success: false, error: response?.data });
            }
        } catch (error) {
            handleSuccess({ success: false, error: error.message });
        }
    };
}
